'use client';
import { useRouter } from 'next/navigation';
import { FormEvent, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { resetPassword } from '@/apis/auth';
import ContainedButton from '@/components/Button/ContainedButton';
import PasswordInput from '@/components/PasswordInput';
import ErrorCircle from '@/icons/ErrorCircle';
import { isPassword } from '@/utils/validation';

import EmailVerify from './_components/EmailVerify';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState<number>();
  const [password, setPassword] = useState('');
  const [passwordRe, setPasswordRe] = useState('');

  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isPasswordReValid, setIsPasswordReValid] = useState(true);

  const isPasswordChecked = useMemo(() => {
    if (!password || !passwordRe) return false;

    return password === passwordRe;
  }, [password, passwordRe]);

  const router = useRouter();

  const clearPassword = () => setPassword('');
  const clearPasswordRe = () => setPasswordRe('');
  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 20) return;
    setPassword(e.target.value);
  };

  const handleChangePasswordRe = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 20) return;
    setPasswordRe(e.target.value);
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();

    if (!code || !password || !passwordRe) return;
    if (!isPasswordValid) {
      toast.error('비밀번호 형식을 확인해주세요');
    }

    if (password !== passwordRe || !isPasswordReValid) {
      toast.error('비밀번호가 일치하지 않습니다');
      return;
    }

    const res = await resetPassword({
      email,
      code,
      password,
    });
    if (!res.ok || !res) {
      toast.error('비밀번호 재설정에 실패했습니다');
      return;
    }

    window.alert('비밀번호 재설정이 완료되었습니다');
    router.push('/auth/sign-in');
  };

  const handleBlurPassword = () => {
    if (!password) {
      setIsPasswordValid(true);
      return;
    }
    if (isPassword(password)) {
      setIsPasswordValid(true);
    } else {
      setIsPasswordValid(false);
    }
  };

  const handleBlurPasswordRe = () => {
    if (password === passwordRe) {
      setIsPasswordReValid(true);
    } else {
      setIsPasswordReValid(false);
    }
  };

  return (
    <form
      className="flex min-h-[calc(100dvh-80px)] flex-col items-center justify-center pt-8 pb-24 md:p-12"
      onSubmit={handleResetPassword}
    >
      <div className="max-w-[488px] w-full px-4">
        <h3 className="text-6xl font-bold text-center">비밀번호 재설정</h3>

        <EmailVerify email={email} setEmail={setEmail} code={code} setCode={setCode} />

        <div className="flex flex-col gap-2.5 mt-5">
          <label htmlFor="pw" className="font-bold flex gap-4 items-end">
            새 비밀번호
            <span className="text-secondary text-xs flex gap-1 font-medium">
              *영문+숫자+특수문자 조합 8~20자
            </span>
          </label>
          <PasswordInput
            id="pw"
            placeholder="비밀번호"
            required
            onChange={handleChangePassword}
            onBlur={handleBlurPassword}
            value={password}
            clear={clearPassword}
            className={!isPasswordValid ? 'ring-2 ring-error' : ''}
          />
          {!isPasswordValid && (
            <div className="mt-1 text-error text-xs font-medium flex gap-2 items-center">
              <ErrorCircle /> 비밀번호 형식을 확인해 주세요
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2.5 mt-5">
          <label htmlFor="pw" className="font-bold flex gap-4 items-end">
            새 비밀번호 확인
          </label>
          <PasswordInput
            id="pw-re"
            placeholder="비밀번호를 한번 더 입력해 주세요"
            required
            onBlur={handleBlurPasswordRe}
            onChange={handleChangePasswordRe}
            value={passwordRe}
            clear={clearPasswordRe}
            className={!isPasswordReValid ? 'ring-2 ring-error' : ''}
          />

          {!isPasswordReValid && (
            <div className="mt-1 text-error text-xs font-medium flex gap-2 items-center">
              <ErrorCircle /> 비밀번호가 일치하지 않습니다
            </div>
          )}

          <div className="hidden my-2 text-error text-xs font-medium peer-invalid:block">
            영문 + 숫자 + 특수문자 조합 8~20자
          </div>
        </div>

        <div className="mt-8">
          <ContainedButton
            className="w-full text-lg h-14 text-white"
            color="primary"
            type="submit"
            disabled={
              !email || !code || !isPasswordChecked || !isPasswordValid || !isPasswordReValid
            }
          >
            완료
          </ContainedButton>
        </div>
      </div>
    </form>
  );
};

export default ForgotPasswordPage;
